import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import { useCallback, useRef } from 'react'
import { getContentItemViewNodeElementMapping } from '../../../utils/TrackChangesPanelUtils'
import { tabIndex, TabName } from './useHandleActiveTab'

export const useHandleLatestContentItem = () => {
    const {
        contentItemViewNodes,
        setIsTreeLoading,
        setArticlesLoading,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setIsTreeLoading: state.actions.setIsTreeLoading,
        setArticlesLoading: state.actions.setArticlesLoading,
    }))

    const loadingNodesRef = useRef<Set<string>>(new Set())
    const addToLoadingState = useCallback(
        (nodeId: string) => {
            loadingNodesRef.current.add(nodeId)
            const newLoadingArray = [...Array.from(loadingNodesRef.current)]
            setArticlesLoading(newLoadingArray)
        },
        [setArticlesLoading]
    )

    const removeFromLoadingState = useCallback(() => {
        loadingNodesRef.current.clear()
        setArticlesLoading([])
    }, [setArticlesLoading])

    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            removeFromLoadingState()
            await onContentViewFetchSuccess(response, [], false)
        },
        onError: () => {
            removeFromLoadingState()
            setIsTreeLoading(false)
        },
    })

    const handleLatestContentItem = useCallback(
        async (trackChange: TrackedChange): Promise<boolean> => {
            const contentItemMapping = getContentItemViewNodeElementMapping(
                contentItemViewNodes
            )
            const contentItemId = trackChange.contentItemElementId
            const hasContentItem = Object.keys(contentItemMapping).some((key) =>
                key.includes(contentItemId)
            )
            if (hasContentItem) {
                return true
            }

            const rootContentItemId = trackChange.rootContentItemId
            const tab = trackChange?.partType?.toLowerCase() as TabName

            const isArticleLevel =
                trackChange.contentItemId === trackChange.rootContentItemId
            if (loadingNodesRef.current.has(rootContentItemId)) {
                return false
            }

            try {
                if (!isArticleLevel) {
                    addToLoadingState(rootContentItemId)
                    await getContentItemViewNodesAPI(
                        [rootContentItemId],
                        tabIndex[tab]
                    )
                }
            } catch (error) {
                console.error('Error in handleLatestContentItem:', error)
                removeFromLoadingState()
                setIsTreeLoading(false)
                return false
            }
            return false
        },
        [
            contentItemViewNodes,
            getContentItemViewNodesAPI,
            setIsTreeLoading,
            addToLoadingState,
            removeFromLoadingState,
        ]
    )

    return handleLatestContentItem
}
